import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { CreditCard, Download, RefreshCcw, Search } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import "./PaieCyclePages.css";

const userName = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const money = (value) => {
    const n = Number(value || 0);
    const fixed = n.toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${intFormatted},${decPart}`;
};

const amount = (value) => Number(value || 0);
const monthLabel = (periode) => periode ? `${String(periode.mois).padStart(2, "0")}/${periode.annee}` : "-";

const PRIMARY = [32, 67, 133];    // #204385

const numberToWordsFR = (value) => {
    const units = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf"];
    const teens = ["dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];
    const tens = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante-dix", "quatre-vingt", "quatre-vingt-dix"];
    const parseUnderThousand = (num) => {
        let text = "";
        const hundred = Math.floor(num / 100);
        const rest = num % 100;
        if (hundred) text += `${hundred === 1 ? "" : `${units[hundred]} `}cent${hundred > 1 && rest === 0 ? "s" : ""} `;
        if (rest < 10) text += units[rest];
        else if (rest < 20) text += teens[rest - 10];
        else {
            const ten = Math.floor(rest / 10);
            const unit = rest % 10;
            text += tens[ten] + (unit === 1 ? "-et-un" : unit > 1 ? `-${units[unit]}` : "");
        }
        return text.trim();
    };
    let n = Math.floor(amount(value));
    if (!n) return "zero Dirhams";
    let text = "";
    if (n >= 1000) {
        const thousand = Math.floor(n / 1000);
        text += `${thousand === 1 ? "" : `${parseUnderThousand(thousand)} `}mille `;
        n %= 1000;
    }
    if (n) text += parseUnderThousand(n);
    return `${text.trim()} Dirhams`;
};

export default function OrdreDeVirementPage() {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [selectedPeriodeId, setSelectedPeriodeId] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("VALIDE");
    const [matriculeDebut, setMatriculeDebut] = useState("");
    const [matriculeFin, setMatriculeFin] = useState("");

    useEffect(() => {
        dispatch(getAllUsersRequest());
        ["periodes", "bulletins", "remunerations"].forEach((resource) => dispatch(payrollFetchResourceRequest(resource)));
    }, [dispatch]);

    useEffect(() => {
        if (!selectedPeriodeId && payroll.periodes?.length > 0) {
            setSelectedPeriodeId(String(payroll.periodes[0].id));
        }
    }, [payroll.periodes, selectedPeriodeId]);

    const activePeriod = useMemo(() => (payroll.periodes || []).find((p) => String(p.id) === String(selectedPeriodeId)), [payroll.periodes, selectedPeriodeId]);
    const bulletins = useMemo(() => (payroll.bulletins || []).filter((b) => (!selectedPeriodeId || String(b.periodeId) === String(selectedPeriodeId)) && (statusFilter === "TOUS" || (statusFilter === "NON_VALIDES" ? b.statut !== "VALIDE" : b.statut === "VALIDE"))), [payroll.bulletins, selectedPeriodeId, statusFilter]);
    
    const remunerationByUser = useMemo(() => {
        const map = {};
        (payroll.remunerations || []).forEach((rem) => {
            if (!map[rem.userId]) map[rem.userId] = rem;
        });
        return map;
    }, [payroll.remunerations]);

    const filteredBulletins = useMemo(() => {
        return bulletins.filter((b) => {
            const user = users.find((u) => u.id === b.userId);
            const code = user?.employeeCode || b.employeeCode || "";
            const nameMatch = !q.trim() || userName(user).toLowerCase().includes(q.toLowerCase());
            const startOk = !matriculeDebut.trim() || code.toLowerCase() >= matriculeDebut.trim().toLowerCase();
            const endOk = !matriculeFin.trim() || code.toLowerCase() <= matriculeFin.trim().toLowerCase();
            return nameMatch && startOk && endOk;
        });
    }, [bulletins, users, q, matriculeDebut, matriculeFin]);

    const downloadVirements = () => {
        if (!filteredBulletins.length) {
            Swal.fire({ icon: "warning", text: "Aucun virement a exporter." });
            return;
        }

        const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const today = new Date();
        const dateStr = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;

        // ── En-tête : date alignée à droite ──────────────────────────────────────
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Casablanca, le : ${dateStr}`, 198, 20, { align: "right" });

        // ── Destinataire ─────────────────────────────────────────────────────────
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("À l\u2019intention de l\u2019agence Attijariwafa Bank.", 198, 38, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("Boulevard Al Qods, Casablanca", 198, 44, { align: "right" });

        // ── Objet ────────────────────────────────────────────────────────────────
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Objet : ", 12, 60);
        doc.setFont("helvetica", "normal");
        doc.text("Ordre de virement", 28, 60);

        // ── Formule de politesse ─────────────────────────────────────────────────
        doc.setFontSize(10);
        doc.text("Madame, Monsieur,", 12, 72);

        // ── Corps : numéro de compte en gras ─────────────────────────────────────
        const accountNumber = "007 532 63520002 41856987 04";
        const bodyLine = "Par le d\u00e9bit de mon compte n\u00b0 ";
        doc.setFont("helvetica", "normal");
        doc.text(bodyLine, 12, 82);
        const bodyLineWidth = doc.getTextWidth(bodyLine);
        doc.setFont("helvetica", "bold");
        doc.text(accountNumber, 12 + bodyLineWidth, 82);

        doc.setFont("helvetica", "normal");
        doc.text("Je vous prie de bien vouloir effectuer les virements suivants :", 12, 92);

        // ── Tableau ───────────────────────────────────────────────────────────────
        const rows = filteredBulletins.map((b, idx) => {
            const u = users.find((user) => user.id === b.userId);
            const rem = remunerationByUser[b.userId] || {};
            return [
                idx + 1,
                money(b.netAPayer),
                userName(u),
                rem.rib || "-",
                [rem.banque].filter(Boolean).join(" / ") || "-",
            ];
        });

        const total = filteredBulletins.reduce((sum, b) => sum + amount(b.netAPayer), 0);

        autoTable(doc, {
            startY: 98,
            margin: { left: 12, right: 12 },
            theme: "grid",
            styles: {
                fontSize: 9,
                cellPadding: 3,
                lineColor: [0, 0, 0],      // noir au lieu de [180, 180, 180]
                lineWidth: 0.3,             // un peu plus épais
                textColor: [30, 30, 30],
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: "bold",
                halign: "center",
                lineColor: [0, 0, 0],      // noir
                lineWidth: 0.3,
            },
            columnStyles: {
                0: { cellWidth: 14, halign: "center" },   // Seq
                1: { cellWidth: 32, halign: "right" },    // Montant
                2: { cellWidth: 42, halign: "left" },     // Bénéficiaire (réduit de 52 → 42)
                3: { cellWidth: 52, halign: "left" },     // RIB
                4: { cellWidth: 36, halign: "left" },     // Banque
            },
            head: [["Seq.", "Montant (MAD)", "B\u00e9n\u00e9ficiaire", "RIB", "Banque"]],
            body: rows
        });

        // ── Montant en lettres ────────────────────────────────────────────────────
        const afterTable = doc.lastAutoTable.finalY + 10;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text(`Montant total des virements en lettres : ${numberToWordsFR(total).toUpperCase()}`, 12, afterTable);

        // ── Signature ─────────────────────────────────────────────────────────────
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("Signature / Cachet", 150, afterTable + 30);

        doc.save(`Ordre_Virement_${monthLabel(activePeriod).replace('/', '_')}.pdf`);
    };

    const btnPrimary = { background: "#204385", color: "#fff", border: "none" };
    const btnAccent  = { background: "#334785", color: "#fff", border: "none" };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><CreditCard size={18} /> Ordre de virement</h2>
                    <p>Visualisation et telechargement de l'état des virements de paie.</p>
                </div>
                <button className="pcyc-btn" style={btnPrimary} type="button" onClick={() => ["bulletins", "remunerations"].forEach((r) => dispatch(payrollFetchResourceRequest(r)))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>

            <div className="pcyc-toolbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ minWidth: "200px" }}>
                        <span>Période</span>
                        <select value={selectedPeriodeId} onChange={(e) => setSelectedPeriodeId(e.target.value)}>
                            <option value="">Sélectionner</option>
                            {(payroll.periodes || []).map((p) => <option key={p.id} value={p.id}>{monthLabel(p)}</option>)}
                        </select>
                    </label>
                    <label><span>Statut</span><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="VALIDE">Validés</option><option value="NON_VALIDES">Non validés</option><option value="TOUS">Tous</option></select></label>
                    <label style={{ width: "120px" }}><span>Matricule debut</span><input value={matriculeDebut} onChange={(e) => setMatriculeDebut(e.target.value)} /></label>
                    <label style={{ width: "120px" }}><span>Matricule fin</span><input value={matriculeFin} onChange={(e) => setMatriculeFin(e.target.value)} /></label>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    <button className="pcyc-btn" style={btnAccent} type="button" onClick={downloadVirements}><Download size={15} /> Télécharger l'ordre de virements</button>
                </div>
            </div>

            <div className="pcyc-toolbar" style={{ borderBottom: "none", borderTopLeftRadius: "10px", borderTopRightRadius: "10px", margin: "14px 18px 0", background: "#f8fafc" }}>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    <button className="pcyc-btn" style={btnPrimary}><CreditCard size={14} /> Ordre de virement ({filteredBulletins.length})</button>
                </div>
                <div className="pcyc-search" style={{ minWidth: "220px", marginLeft: "auto" }}>
                    <Search size={14} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher collaborateur..." style={{ padding: "6px 10px 6px 30px" }} />
                </div>
            </div>

            <div className="pcyc-table-card" style={{ marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <table className="pcyc-table">
                    <thead>
                    <tr><th>Matricule</th><th>Beneficiaire</th><th>RIB</th><th>Banque / Agence</th><th>Montant Net</th><th>Statut</th></tr>
                    </thead>
                    <tbody>
                    {filteredBulletins.length === 0
                        ? <tr><td colSpan="6" className="pcyc-empty">Aucun virement pour cette periode</td></tr>
                        : filteredBulletins.map((b) => {
                            const u = users.find((user) => user.id === b.userId);
                            const rem = remunerationByUser[b.userId] || {};
                            return (
                                <tr key={b.id}>
                                    <td><span className="pcyc-code">{u?.employeeCode || b.employeeCode || "N/A"}</span></td>
                                    <td><strong>{userName(u)}</strong></td>
                                    <td><span style={{ fontFamily: "monospace", fontSize: "11px" }}>{rem.rib || "N/A"}</span></td>
                                    <td>{[rem.banque, rem.agence].filter(Boolean).join(" - ") || "N/A"}</td>
                                    <td><strong>{money(b.netAPayer)} MAD</strong></td>
                                    <td>{rem.rib
                                        ? <span style={{ color: "#204385", fontSize: "11px", fontWeight: 700 }}>Prêt pour virement</span>
                                        : <span style={{ color: "#334785", fontSize: "11px", fontWeight: 700 }}>RIB manquant</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
